import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../api";

const initialState = { name: "", code: "", description: "" };

// Quick "+ New Module" flow launched from the Create Work Ticket form when
// the module a user needs isn't in the selected project's list yet. Reuses
// the EXISTING project-module-create API (POST /projects/:projectId/modules,
// the same admin Project Module Management endpoint) -- no duplicate module
// storage, no duplicated validation. A module always belongs to a project,
// so this can only be opened once a project is selected. Visibility is
// gated by the caller via <Can moduleKey="project_modules" actionKey="create">.
export default function QuickCreateModuleModal({ show, projectId, projectName, onClose, onCreated }) {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const reset = () => setForm(initialState);

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!projectId) return;
    if (!form.name.trim()) {
      toast.warning("Module name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/projects/${projectId}/modules`, {
        name: form.name.trim(),
        code: form.code.trim() || undefined,
        description: form.description.trim() || null,
      });
      toast.success("Module created");
      reset();
      onCreated(res.data?.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create module");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="qcp-modal">
      <Modal.Header closeButton>
        <Modal.Title>New Module</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row g-3">
          <div className="col-md-12">
            <Form.Label>Project</Form.Label>
            <Form.Control value={projectName || ""} disabled readOnly />
          </div>
          <div className="col-md-8">
            <Form.Label>Module Name *</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g. Billing"
              autoFocus
            />
          </div>
          <div className="col-md-4">
            <Form.Label>Module Code</Form.Label>
            <Form.Control
              value={form.code}
              onChange={(e) => handleChange("code", e.target.value)}
              placeholder="Auto"
            />
            <Form.Text className="text-muted">Leave blank to auto-generate.</Form.Text>
          </div>
          <div className="col-md-12">
            <Form.Label>Description (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="What does this module cover?"
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button className="unit-btn unit-btn-primary" onClick={handleCreate} disabled={saving || !projectId}>
          {saving ? "Creating..." : "Create Module"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
